import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  HabitaButton,
  HabitaChartTooltipSurface,
  HabitaDialog,
  HabitaEmptyState,
  HabitaErrorNotice,
  HabitaSectionHeader,
  HabitaSelect,
  HabitaViewToggle,
  classNames,
  habitaSurfaceClass,
} from '../../../shared/components/habita';
import type {
  MarketingInterestRegionsResponse,
  MarketingOtherInterestLocality,
  MarketingRow,
} from '../../../shared/models';

type InterestMetric = 'leads_interesse' | 'pastas_aprovadas';
type InterestRegionsView = 'bars' | 'donut' | 'table';

interface InterestRegionRow {
  adId: string;
  adName: string;
  adsetId: string;
  adsetName: string;
  approvedFolders: number;
  campaignId: string;
  campaignName: string;
  city: string;
  creativeId: string;
  creativeName: string;
  leadsInterest: number;
  missingLocation: boolean;
  otherLocation: boolean;
}

interface CampaignSeries {
  color: string;
  id: string;
  key: string;
  label: string;
}

interface InterestChartDatum {
  [key: string]: number | string | boolean;
  city: string;
  missingLocation: boolean;
  otherLocation: boolean;
  total: number;
}

interface RegionHierarchyRow {
  children: RegionHierarchyRow[];
  id: string;
  label: string;
  level: 'adset' | 'campaign' | 'creative' | 'region';
  missingLocation: boolean;
  otherLocation: boolean;
  values: Record<string, number>;
}

const numberFormatter = new Intl.NumberFormat('pt-BR');
const OTHER_LOCALITIES_LABEL = 'Outras localidades';
const MISSING_REGION_LABEL = 'Região não informada';
const campaignPalette = [
  '#2F5D8A',
  '#1E8A76',
  '#C98A3D',
  '#8B4A6B',
  '#4F7C3B',
  '#B85450',
  '#526D82',
];
const cityPalette: Record<string, string> = {
  'Águas Lindas, GO': '#E8A33D',
  'Valparaíso de Goiás, GO': '#1F6B4F',
  'Luziânia, GO': '#E0A72E',
  'Novo Gama, GO': '#E2574C',
  'Cidade Ocidental, GO': '#3B7DD8',
  'Samambaia, DF': '#7A5CC0',
  'Anápolis, GO': '#E06A2E',
  'Santo Antônio do Descoberto, GO': '#2E9E86',
  [OTHER_LOCALITIES_LABEL]: '#7C847E',
  [MISSING_REGION_LABEL]: '#A3AAA5',
};

const metricOptions = [
  { label: 'Leads de interesse', value: 'leads_interesse' },
  { label: 'Pastas aprovadas', value: 'pastas_aprovadas' },
];
const viewOptions = [
  { icon: 'ri-bar-chart-horizontal-line', label: 'Barras', value: 'bars' as const },
  { icon: 'ri-donut-chart-line', label: 'Donut', value: 'donut' as const },
  { icon: 'ri-table-line', label: 'Tabela', value: 'table' as const },
];

function cityColor(city: string, index: number): string {
  return cityPalette[city] ?? campaignPalette[index % campaignPalette.length];
}

function textValue(row: MarketingRow, key: string, fallback: string): string {
  const value = row[key];
  if (value === null || value === undefined || String(value).trim() === '') return fallback;
  return String(value).trim();
}

function numberValue(row: MarketingRow, key: string): number {
  const numeric = Number(row[key]);
  return Number.isFinite(numeric) ? numeric : 0;
}

function booleanValue(row: MarketingRow, key: string): boolean {
  const value = row[key];
  return value === true || value === 1 || value === '1' || value === 'true';
}

function normalizeRows(rows: MarketingRow[]): InterestRegionRow[] {
  return rows.map((row) => {
    const sourceCity = textValue(row, 'cidade_interesse', MISSING_REGION_LABEL);
    const explicitlyOtherLocation = booleanValue(row, 'outra_localidade');
    const missingLocation = !explicitlyOtherLocation && (
      booleanValue(row, 'regiao_nao_informada')
      || sourceCity === MISSING_REGION_LABEL
    );
    const otherLocation = explicitlyOtherLocation || (!missingLocation && (
      sourceCity === 'Outra localidade'
      || sourceCity === OTHER_LOCALITIES_LABEL
    ));

    return {
      adId: textValue(row, 'ad_id', 'sem-anuncio'),
      adName: textValue(row, 'ad_name', 'Anúncio não identificado'),
      adsetId: textValue(row, 'adset_id', 'sem-adset'),
      adsetName: textValue(row, 'adset_name', 'Adset não identificado'),
      approvedFolders: numberValue(row, 'pastas_aprovadas'),
      campaignId: textValue(row, 'campaign_id', 'sem-campanha'),
      campaignName: textValue(row, 'campaign_name', 'Campanha não identificada'),
      city: missingLocation ? MISSING_REGION_LABEL : otherLocation ? OTHER_LOCALITIES_LABEL : sourceCity,
      creativeId: textValue(row, 'creative_id', textValue(row, 'ad_id', 'sem-criativo')),
      creativeName: textValue(row, 'creative_name', textValue(row, 'ad_name', 'Criativo não identificado')),
      leadsInterest: numberValue(row, 'leads_interesse'),
      missingLocation,
      otherLocation,
    };
  });
}

function metricValue(row: InterestRegionRow, metric: InterestMetric): number {
  return metric === 'pastas_aprovadas' ? row.approvedFolders : row.leadsInterest;
}

function metricLabel(metric: InterestMetric): string {
  return metric === 'pastas_aprovadas' ? 'Pastas aprovadas' : 'Leads de interesse';
}

function metricUnit(metric: InterestMetric, value: number): string {
  if (metric === 'pastas_aprovadas') return value === 1 ? 'pasta aprovada' : 'pastas aprovadas';
  return value === 1 ? 'lead de interesse' : 'leads de interesse';
}

function orderedCities(rows: InterestRegionRow[], metric: InterestMetric): string[] {
  const totals = new Map<string, number>();
  rows.forEach((row) => totals.set(row.city, (totals.get(row.city) ?? 0) + metricValue(row, metric)));

  return [...totals.keys()].sort((left, right) => {
    if (left === right) return 0;
    if (left === MISSING_REGION_LABEL) return 1;
    if (right === MISSING_REGION_LABEL) return -1;
    if (left === OTHER_LOCALITIES_LABEL) return 1;
    if (right === OTHER_LOCALITIES_LABEL) return -1;
    return (totals.get(right) ?? 0) - (totals.get(left) ?? 0)
      || left.localeCompare(right, 'pt-BR');
  });
}

function buildCampaignSeries(rows: InterestRegionRow[]): CampaignSeries[] {
  const campaignTotals = new Map<string, {
    approvedFolders: number;
    label: string;
    leadsInterest: number;
  }>();

  rows.forEach((row) => {
    const current = campaignTotals.get(row.campaignId);
    campaignTotals.set(row.campaignId, {
      approvedFolders: (current?.approvedFolders ?? 0) + row.approvedFolders,
      label: row.campaignName,
      leadsInterest: (current?.leadsInterest ?? 0) + row.leadsInterest,
    });
  });

  const ordered = [...campaignTotals.entries()]
    .sort(([, left], [, right]) => (
      right.leadsInterest - left.leadsInterest
      || right.approvedFolders - left.approvedFolders
      || left.label.localeCompare(right.label, 'pt-BR')
    ));
  const visible = ordered.slice(0, campaignPalette.length);
  const hasRemainder = ordered.length > visible.length;

  return [
    ...visible.map(([id, item], index) => ({
      color: campaignPalette[index],
      id,
      key: `campaign_${index}`,
      label: item.label,
    })),
    ...(hasRemainder ? [{
      color: '#7C847E',
      id: '__other_campaigns__',
      key: 'campaign_other',
      label: 'Outras campanhas',
    }] : []),
  ];
}

function buildChartData(
  rows: InterestRegionRow[],
  cities: string[],
  series: CampaignSeries[],
  metric: InterestMetric,
): InterestChartDatum[] {
  const visibleIds = new Set(series.filter((item) => item.id !== '__other_campaigns__').map((item) => item.id));
  const seriesByCampaign = new Map(series.map((item) => [item.id, item]));
  const otherSeries = seriesByCampaign.get('__other_campaigns__');

  return cities.map((city) => {
    const datum: InterestChartDatum = {
      city,
      missingLocation: city === MISSING_REGION_LABEL,
      otherLocation: city === OTHER_LOCALITIES_LABEL,
      total: 0,
    };
    series.forEach((item) => { datum[item.key] = 0; });

    rows.filter((row) => row.city === city).forEach((row) => {
      const value = metricValue(row, metric);
      const target = visibleIds.has(row.campaignId)
        ? seriesByCampaign.get(row.campaignId)
        : otherSeries;
      if (!target) return;
      datum[target.key] = Number(datum[target.key] ?? 0) + value;
      datum.total = Number(datum.total) + value;
    });

    return datum;
  });
}

function InterestRegionsTooltip({
  active,
  payload,
  series,
  metric,
}: {
  active?: boolean;
  payload?: Array<{ payload?: InterestChartDatum }>;
  series: CampaignSeries[];
  metric: InterestMetric;
}) {
  const datum = payload?.[0]?.payload;
  if (!active || !datum) return null;

  const breakdown = series
    .map((item) => ({ ...item, value: Number(datum[item.key] ?? 0) }))
    .filter((item) => item.value > 0)
    .sort((left, right) => right.value - left.value);

  return (
    <HabitaChartTooltipSurface className="min-w-64 max-w-[min(360px,calc(100vw-1rem))] p-3">
      <p className="mb-2 font-semibold text-textPrimary">{datum.city}</p>
      <div className="space-y-1.5">
        {breakdown.map((item) => (
          <p key={item.key} className="flex items-start justify-between gap-4 text-xs">
            <span className="flex min-w-0 items-start gap-2 text-textSecondary">
              <span
                className="mt-1 h-2 w-2 shrink-0 rounded-sm"
                style={{ backgroundColor: item.color }}
                aria-hidden
              />
              <span className="break-words">{item.label}</span>
            </span>
            <strong className="shrink-0 font-mono text-textPrimary">{numberFormatter.format(item.value)}</strong>
          </p>
        ))}
      </div>
      <p className="mt-2 flex justify-between gap-4 border-t border-border-subtle pt-2 text-xs">
        <span className="text-textSecondary">Total de {metricLabel(metric).toLowerCase()}</span>
        <strong className="font-mono text-textPrimary">{numberFormatter.format(Number(datum.total))}</strong>
      </p>
    </HabitaChartTooltipSurface>
  );
}

function sumValues(values: Record<string, number>): number {
  return Object.values(values).reduce((total, value) => total + value, 0);
}

function groupRows(
  rows: InterestRegionRow[],
  keySelector: (row: InterestRegionRow) => string,
): Map<string, InterestRegionRow[]> {
  const groups = new Map<string, InterestRegionRow[]>();
  rows.forEach((row) => {
    const key = keySelector(row);
    groups.set(key, [...(groups.get(key) ?? []), row]);
  });
  return groups;
}

function aggregateByCampaignSeries(
  rows: InterestRegionRow[],
  series: CampaignSeries[],
  metric: InterestMetric,
): Record<string, number> {
  const values = Object.fromEntries(series.map((item) => [item.key, 0]));
  const visibleSeries = new Map(
    series
      .filter((item) => item.id !== '__other_campaigns__')
      .map((item) => [item.id, item]),
  );
  const otherSeries = series.find((item) => item.id === '__other_campaigns__');

  rows.forEach((row) => {
    const target = visibleSeries.get(row.campaignId) ?? otherSeries;
    if (!target) return;
    values[target.key] = (values[target.key] ?? 0) + metricValue(row, metric);
  });

  return values;
}

function sortHierarchyRows(rows: RegionHierarchyRow[]): RegionHierarchyRow[] {
  return rows.sort((left, right) => (
    sumValues(right.values) - sumValues(left.values)
    || left.label.localeCompare(right.label, 'pt-BR')
  ));
}

function buildRegionHierarchy(
  rows: InterestRegionRow[],
  cities: string[],
  series: CampaignSeries[],
  metric: InterestMetric,
): RegionHierarchyRow[] {
  return cities.map((city) => {
    const cityRows = rows.filter((row) => row.city === city);
    const campaigns = groupRows(cityRows, (row) => row.campaignId);
    const campaignChildren = [...campaigns.entries()].map(([campaignId, campaignRows]) => {
      const adsets = groupRows(campaignRows, (row) => row.adsetId);
      const adsetChildren = [...adsets.entries()].map(([adsetId, adsetRows]) => {
        const creatives = groupRows(adsetRows, (row) => `${row.adId}:${row.creativeId}`);
        const creativeChildren = [...creatives.entries()].map(([creativeId, creativeRows]) => ({
          children: [],
          id: `region:${city}:campaign:${campaignId}:adset:${adsetId}:creative:${creativeId}`,
          label: creativeRows[0]?.creativeName === creativeRows[0]?.adName
            ? creativeRows[0]?.adName ?? 'Criativo não identificado'
            : `${creativeRows[0]?.adName ?? 'Anúncio não identificado'} · ${creativeRows[0]?.creativeName ?? 'Criativo não identificado'}`,
          level: 'creative' as const,
          missingLocation: city === MISSING_REGION_LABEL,
          otherLocation: city === OTHER_LOCALITIES_LABEL,
          values: aggregateByCampaignSeries(creativeRows, series, metric),
        }));

        return {
          children: sortHierarchyRows(creativeChildren),
          id: `region:${city}:campaign:${campaignId}:adset:${adsetId}`,
          label: adsetRows[0]?.adsetName ?? 'Adset não identificado',
          level: 'adset' as const,
          missingLocation: city === MISSING_REGION_LABEL,
          otherLocation: city === OTHER_LOCALITIES_LABEL,
          values: aggregateByCampaignSeries(adsetRows, series, metric),
        };
      });

      return {
        children: sortHierarchyRows(adsetChildren),
        id: `region:${city}:campaign:${campaignId}`,
        label: campaignRows[0]?.campaignName ?? 'Campanha não identificada',
        level: 'campaign' as const,
        missingLocation: city === MISSING_REGION_LABEL,
        otherLocation: city === OTHER_LOCALITIES_LABEL,
        values: aggregateByCampaignSeries(campaignRows, series, metric),
      };
    });

    return {
      children: sortHierarchyRows(campaignChildren),
      id: `region:${city}`,
      label: city,
      level: 'region' as const,
      missingLocation: city === MISSING_REGION_LABEL,
      otherLocation: city === OTHER_LOCALITIES_LABEL,
      values: aggregateByCampaignSeries(cityRows, series, metric),
    };
  });
}

function collectExpandableIds(rows: RegionHierarchyRow[]): string[] {
  return rows.flatMap((row) => [
    ...(row.children.length > 0 ? [row.id] : []),
    ...collectExpandableIds(row.children),
  ]);
}

function flattenVisibleRows(
  rows: RegionHierarchyRow[],
  expanded: Set<string>,
): RegionHierarchyRow[] {
  return rows.flatMap((row) => [
    row,
    ...(row.children.length > 0 && expanded.has(row.id)
      ? flattenVisibleRows(row.children, expanded)
      : []),
  ]);
}

function dominantCampaign(
  values: Record<string, number>,
  series: CampaignSeries[],
): { label: string; percentage: number } | null {
  const total = sumValues(values);
  if (total <= 0) return null;
  const dominant = series
    .map((item) => ({ ...item, value: values[item.key] ?? 0 }))
    .sort((left, right) => right.value - left.value)[0];
  if (!dominant || dominant.value <= 0) return null;
  return { label: dominant.label, percentage: (dominant.value / total) * 100 };
}

function InterestRegionsDonutTooltip({
  active,
  metric,
  payload,
  total,
}: {
  active?: boolean;
  metric: InterestMetric;
  payload?: Array<{ payload?: InterestChartDatum }>;
  total: number;
}) {
  const datum = payload?.[0]?.payload;
  if (!active || !datum) return null;
  const value = Number(datum.total);

  return (
    <HabitaChartTooltipSurface className="min-w-52 p-3">
      <p className="font-semibold text-textPrimary">{datum.city}</p>
      <p className="mt-2 flex items-center justify-between gap-4 text-xs">
        <span className="text-textSecondary">{metricLabel(metric)}</span>
        <strong className="font-mono text-textPrimary">{numberFormatter.format(value)}</strong>
      </p>
      <p className="mt-1 flex items-center justify-between gap-4 text-xs">
        <span className="text-textSecondary">Participação</span>
        <strong className="font-mono text-textPrimary">
          {total > 0 ? `${((value / total) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : '0%'}
        </strong>
      </p>
    </HabitaChartTooltipSurface>
  );
}

function InterestRegionsDonut({
  data,
  metric,
}: {
  data: InterestChartDatum[];
  metric: InterestMetric;
}) {
  const visibleData = data.filter((item) => Number(item.total) > 0);
  const total = visibleData.reduce((sum, item) => sum + Number(item.total), 0);

  if (visibleData.length === 0) {
    return (
      <div className="p-4">
        <HabitaEmptyState
          icon="ri-donut-chart-line"
          title="Sem volume nas cidades cobertas"
          description={`Não há ${metricLabel(metric).toLowerCase()} nas cidades cobertas para os filtros atuais.`}
        />
      </div>
    );
  }

  return (
    <div className="grid items-center gap-4 p-4 lg:grid-cols-[minmax(300px,0.8fr)_minmax(320px,1.2fr)] lg:px-6">
      <div className="relative mx-auto h-72 w-full max-w-md">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <PieChart>
            <Pie
              data={visibleData}
              dataKey="total"
              innerRadius="54%"
              nameKey="city"
              outerRadius="82%"
              paddingAngle={1}
            >
              {visibleData.map((item, index) => (
                <Cell key={item.city} fill={cityColor(item.city, index)} />
              ))}
            </Pie>
            <Tooltip
              content={<InterestRegionsDonutTooltip metric={metric} total={total} />}
              wrapperStyle={{ pointerEvents: 'none', zIndex: 40 }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center font-instrument">
          <strong className="font-mono text-2xl text-textPrimary">{numberFormatter.format(total)}</strong>
          <span className="mt-1 text-center text-xs text-textSecondary">{metricLabel(metric)}</span>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {visibleData.map((item, index) => {
          const value = Number(item.total);
          const percentage = total > 0 ? (value / total) * 100 : 0;
          return (
            <div
              key={item.city}
              className="flex min-w-0 items-center gap-2 rounded-md border border-border-subtle px-3 py-2 font-instrument text-xs"
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: cityColor(item.city, index) }}
                aria-hidden
              />
              <span className="min-w-0 flex-1 break-words text-textSecondary">{item.city}</span>
              <span className="shrink-0 whitespace-nowrap font-mono font-semibold text-textPrimary">
                {numberFormatter.format(value)} · {percentage.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InterestRegionsMatrix({
  hierarchy,
  onOpenOtherLocalities,
  series,
}: {
  hierarchy: RegionHierarchyRow[];
  onOpenOtherLocalities: () => void;
  series: CampaignSeries[];
}) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const expandableIds = useMemo(() => collectExpandableIds(hierarchy), [hierarchy]);
  const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => expanded.has(id));

  const toggle = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const visibleRows = flattenVisibleRows(hierarchy, expanded);
  const indentation = { region: 0, campaign: 24, adset: 44, creative: 64 } as const;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border-subtle bg-muted/20 px-4 py-2.5">
        <p className="font-instrument text-xs text-textSecondary">
          Regiões nas linhas e campanhas nas colunas. Expanda uma região para ver campanha, adset e criativo.
        </p>
        <HabitaButton
          icon={allExpanded ? 'ri-contract-up-down-line' : 'ri-expand-up-down-line'}
          onClick={() => setExpanded(allExpanded ? new Set() : new Set(expandableIds))}
          size="sm"
          variant="secondary"
        >
          {allExpanded ? 'Recolher tudo' : 'Expandir tudo'}
        </HabitaButton>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-max border-collapse font-instrument text-xs">
          <thead className="border-b border-border-subtle bg-muted text-textSecondary">
            <tr>
              <th className="sticky left-0 z-20 min-w-[290px] max-w-[380px] bg-muted px-4 py-3 text-left font-medium">
                Cidade de interesse
              </th>
              {series.map((item) => (
                <th
                  key={item.key}
                  className="w-32 min-w-32 px-3 py-3 text-right font-medium"
                >
                  <span className="inline-flex max-w-28 items-start justify-end gap-1.5 whitespace-normal leading-4">
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: item.color }} aria-hidden />
                    {item.label}
                  </span>
                </th>
              ))}
              <th className="w-24 min-w-24 px-4 py-3 text-right font-semibold text-textPrimary">Total</th>
              <th className="w-44 min-w-44 px-4 py-3 text-left font-semibold text-textPrimary">Campanha principal</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {visibleRows.map((row) => {
              const canExpand = row.children.length > 0;
              const isExpanded = expanded.has(row.id);
              const primaryCampaign = row.level === 'region' ? dominantCampaign(row.values, series) : null;
              return (
                <tr
                  key={row.id}
                  className={classNames(
                    'transition-colors hover:bg-accent',
                    row.level === 'region' && 'bg-muted/25 font-semibold',
                    (row.otherLocation || row.missingLocation) && row.level === 'region' && 'text-textSecondary italic'
                  )}
                >
                  <td
                    className={classNames(
                      'sticky left-0 z-10 max-w-[380px] bg-card px-4 py-3 text-textPrimary',
                      row.level === 'region' && 'bg-[hsl(var(--muted))]',
                      (row.otherLocation || row.missingLocation) && row.level === 'region' && 'text-textSecondary'
                    )}
                  >
                    <div
                      className="flex min-w-0 items-start gap-2"
                      style={{ paddingLeft: indentation[row.level] }}
                    >
                      {canExpand ? (
                        <button
                          type="button"
                          className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-textSecondary transition-colors hover:bg-muted hover:text-textPrimary"
                          aria-label={`${isExpanded ? 'Recolher' : 'Expandir'} ${row.label}`}
                          aria-expanded={isExpanded}
                          onClick={() => toggle(row.id)}
                        >
                          <i className={isExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} aria-hidden />
                        </button>
                      ) : (
                        <span className="h-5 w-5 shrink-0" aria-hidden />
                      )}
                      {row.otherLocation && row.level === 'region' ? (
                        <button
                          type="button"
                          className="min-w-0 break-words text-left leading-5 text-primary underline decoration-dotted underline-offset-2 hover:text-primary/80"
                          onClick={onOpenOtherLocalities}
                        >
                          {row.label}
                        </button>
                      ) : (
                        <span className="min-w-0 break-words leading-5">{row.label}</span>
                      )}
                      {canExpand && (
                        <span className="shrink-0 whitespace-nowrap text-[10px] font-normal text-textSecondary">
                          {row.children.length}{' '}
                          {row.level === 'region'
                            ? 'campanha(s)'
                            : row.level === 'campaign'
                              ? 'adset(s)'
                              : 'criativo(s)'}
                        </span>
                      )}
                    </div>
                  </td>
                  {series.map((item) => (
                    <td
                      key={item.key}
                      className="px-3 py-3 text-right font-mono tabular-nums text-textPrimary"
                    >
                      {numberFormatter.format(row.values[item.key] ?? 0)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums text-textPrimary">
                    {numberFormatter.format(sumValues(row.values))}
                  </td>
                  <td className="px-4 py-3 text-left text-textSecondary">
                    {primaryCampaign ? (
                      <span className="inline-flex max-w-40 items-center gap-1.5 rounded-full border border-border-subtle px-2 py-1 not-italic">
                        <span className="min-w-0 truncate">{primaryCampaign.label}</span>
                        <strong className="shrink-0 font-mono text-textPrimary">
                          {primaryCampaign.percentage.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
                        </strong>
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="border-t border-border-subtle px-5 py-3 font-instrument text-xs text-textSecondary">
        “Outras localidades” reúne cidades informadas fora das áreas cobertas. “Região não informada” identifica contatos sem cidade de interesse e não participa do cálculo da campanha em destaque.
      </p>
    </div>
  );
}

function SeparatedLocationSummary({
  datum,
  description,
  label,
  metric,
  onClick,
  series,
}: {
  datum?: InterestChartDatum;
  description: string;
  label: string;
  metric: InterestMetric;
  onClick?: () => void;
  series: CampaignSeries[];
}) {
  const total = Number(datum?.total ?? 0);
  if (!datum || total <= 0) return null;

  return (
    <div className="mx-4 mb-4 rounded-md border border-border-subtle bg-muted/35 px-4 py-3 font-instrument sm:mx-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          {onClick ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary underline decoration-dotted underline-offset-2 hover:text-primary/80"
              onClick={onClick}
            >
              {label}
              <i className="ri-arrow-right-up-line" aria-hidden />
            </button>
          ) : (
            <p className="text-xs font-semibold text-textPrimary">{label}</p>
          )}
          <p className="mt-0.5 text-[11px] text-textSecondary">
            {description}
          </p>
        </div>
        <strong className="font-mono text-sm text-textPrimary">
          {numberFormatter.format(total)} {metricUnit(metric, total)}
        </strong>
      </div>
      <div className="mt-3 flex h-2.5 overflow-hidden rounded-full bg-border-subtle" aria-hidden>
        {series.map((item) => {
          const value = Number(datum[item.key] ?? 0);
          return value > 0 ? (
            <span
              key={item.key}
              style={{ backgroundColor: item.color, width: `${(value / total) * 100}%` }}
            />
          ) : null;
        })}
      </div>
    </div>
  );
}

export function OtherLocalitiesDialog({
  contextLabel,
  error,
  isOpen,
  isLoading = false,
  items,
  onClose,
}: {
  contextLabel?: string;
  error?: string | null;
  isOpen: boolean;
  isLoading?: boolean;
  items: MarketingOtherInterestLocality[];
  onClose: () => void;
}) {
  const total = items.reduce((sum, item) => sum + item.occurrences, 0);

  return (
    <HabitaDialog
      description="Cidades de interesse informadas que não pertencem às regiões acompanhadas individualmente. Variações equivalentes são agrupadas."
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title="Outras localidades"
    >
      {contextLabel ? (
        <div className="mb-3 flex min-w-0 items-center gap-2 rounded-md border border-border-subtle bg-muted/45 px-3 py-2 font-instrument text-xs text-textSecondary">
          <i className="ri-filter-3-line shrink-0" aria-hidden />
          <span className="min-w-0 break-words">
            Recorte: <strong className="font-semibold text-textPrimary">{contextLabel}</strong>
          </span>
        </div>
      ) : null}

      {!isLoading && !error ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-border-subtle pb-3 font-instrument text-xs text-textSecondary">
          <span>{numberFormatter.format(items.length)} localidade(s) identificada(s)</span>
          <strong className="font-mono text-sm text-textPrimary">
            {numberFormatter.format(total)} ocorrência(s)
          </strong>
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex min-h-40 items-center justify-center gap-2 font-instrument text-sm text-textSecondary" role="status">
          <i className="ri-loader-4-line animate-spin text-base" aria-hidden />
          Carregando localidades deste item...
        </div>
      ) : error ? (
        <HabitaErrorNotice>{error}</HabitaErrorNotice>
      ) : items.length === 0 ? (
        <HabitaEmptyState
          icon="ri-map-pin-line"
          title="Nenhuma outra localidade identificada"
          description="Não há nomes de cidade fora das regiões acompanhadas para os filtros atuais."
        />
      ) : (
        <div className="divide-y divide-border-subtle">
          {items.map((item, index) => (
            <div key={`${item.name}-${index}`} className="flex items-center justify-between gap-4 py-3 font-instrument">
              <div className="flex min-w-0 items-center gap-3">
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-semibold text-textSecondary">
                  {index + 1}
                </span>
                <span className="min-w-0 break-words text-sm font-medium text-textPrimary">{item.name}</span>
              </div>
              <span className="shrink-0 rounded-full border border-border-subtle bg-muted/45 px-2.5 py-1 font-mono text-xs font-semibold text-textPrimary">
                {numberFormatter.format(item.occurrences)}
              </span>
            </div>
          ))}
        </div>
      )}
    </HabitaDialog>
  );
}

export default function InterestRegionsTab({ data }: { data: MarketingInterestRegionsResponse }) {
  const [metric, setMetric] = useState<InterestMetric>('leads_interesse');
  const [otherLocalitiesOpen, setOtherLocalitiesOpen] = useState(false);
  const [view, setView] = useState<InterestRegionsView>('bars');
  const rows = useMemo(() => normalizeRows(data.rows), [data.rows]);
  const cities = useMemo(() => orderedCities(rows, metric), [metric, rows]);
  const chartRows = useMemo(
    () => rows.filter((row) => !row.otherLocation && !row.missingLocation),
    [rows],
  );
  const chartCities = useMemo(
    () => cities.filter((city) => city !== OTHER_LOCALITIES_LABEL && city !== MISSING_REGION_LABEL),
    [cities],
  );
  const series = useMemo(() => buildCampaignSeries(rows), [rows]);
  const chartData = useMemo(
    () => buildChartData(chartRows, chartCities, series, metric),
    [chartCities, chartRows, metric, series],
  );
  const otherLocationDatum = useMemo(
    () => buildChartData(rows, [OTHER_LOCALITIES_LABEL], series, metric)[0],
    [metric, rows, series],
  );
  const missingLocationDatum = useMemo(
    () => buildChartData(rows, [MISSING_REGION_LABEL], series, metric)[0],
    [metric, rows, series],
  );
  const hierarchy = useMemo(
    () => buildRegionHierarchy(rows, cities, series, metric),
    [cities, metric, rows, series],
  );
  const chartHeight = Math.max(340, chartData.length * 58);

  const leader = useMemo(() => {
    const totals = new Map<string, { label: string; value: number }>();
    rows.filter((row) => !row.otherLocation && !row.missingLocation).forEach((row) => {
      const current = totals.get(row.campaignId);
      totals.set(row.campaignId, {
        label: row.campaignName,
        value: (current?.value ?? 0) + metricValue(row, metric),
      });
    });
    return [...totals.values()].sort((left, right) => right.value - left.value || left.label.localeCompare(right.label, 'pt-BR'))[0];
  }, [metric, rows]);

  const selectedDatum = chartData[0];
  const dominantSeries = selectedDatum
    ? series
      .map((item) => ({ ...item, value: Number(selectedDatum[item.key] ?? 0) }))
      .sort((left, right) => right.value - left.value)[0]
    : undefined;
  const sectionTitle = view === 'bars'
    ? 'Volume por cidade de interesse'
    : view === 'donut'
      ? 'Distribuição por cidade de interesse'
      : 'Detalhamento por campanha';
  const sectionDescription = view === 'bars'
    ? 'Cada barra é uma cidade de interesse; os segmentos mostram a contribuição de cada campanha.'
    : view === 'donut'
      ? `Participação de cada cidade coberta no total de ${metricLabel(metric).toLowerCase()}.`
      : `Regiões em linha e campanhas em coluna, exibindo ${metricLabel(metric).toLowerCase()}.`;

  return (
    <div className="space-y-4">
      <div className="min-w-0">
        <h1 className="flex items-center gap-2 font-fraunces text-2xl font-semibold text-textPrimary">
          Regiões de interesse
          <i
            className="ri-information-line text-base text-textSecondary"
            aria-label="Cidade onde o lead deseja comprar o imóvel"
          />
        </h1>
        <p className="mt-1 font-instrument text-sm text-textSecondary">
          Quais campanhas estão trazendo leads com interesse de compra em cada cidade.
        </p>
      </div>

      {data.notice && (
        <div className="rounded-lg border border-amber-300/45 bg-amber-50 px-4 py-3 font-instrument text-xs text-amber-900 dark:bg-amber-950/25 dark:text-amber-100">
          {data.notice}
        </div>
      )}

      {rows.length > 0 && (
        <article className={classNames(habitaSurfaceClass, 'flex items-center gap-4 border-l-4 border-l-emerald-600 p-4')}>
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
            <i className="ri-bar-chart-grouped-line text-xl" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="font-instrument text-[11px] font-bold uppercase text-textSecondary">
              Campanha com mais {metricLabel(metric).toLowerCase()} em cidades cobertas
            </p>
            {leader ? (
              <p className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 font-instrument">
                <strong className="break-words text-base text-textPrimary">{leader.label}</strong>
                <span className="font-mono text-lg font-bold text-emerald-700 dark:text-emerald-300">
                  {numberFormatter.format(leader.value)}
                </span>
              </p>
            ) : (
              <p className="mt-1 font-instrument text-sm text-textSecondary">Sem volume em cidades cobertas.</p>
            )}
          </div>
        </article>
      )}

      <section className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
        <HabitaSectionHeader
          title={sectionTitle}
          description={sectionDescription}
        >
          <div className="flex flex-wrap items-center justify-end gap-2">
            <label className="flex items-center gap-2 font-instrument text-xs font-semibold text-textSecondary">
              Indicador
              <HabitaSelect
                aria-label="Indicador das regiões de interesse"
                className="min-w-44"
                onChange={(event) => setMetric(event.target.value as InterestMetric)}
                options={metricOptions}
                value={metric}
              />
            </label>
            <HabitaViewToggle
              label="Visualização das regiões de interesse"
              onChange={setView}
              options={viewOptions}
              showLabels
              value={view}
            />
          </div>
        </HabitaSectionHeader>

        {rows.length === 0 ? (
          <div className="p-4">
            <HabitaEmptyState
              icon="ri-map-pin-line"
              title="Sem cidades de interesse no período"
              description="Não há contatos de Marketing com cidade de interesse vinculada aos filtros atuais."
            />
          </div>
        ) : view === 'table' ? (
          <InterestRegionsMatrix
            hierarchy={hierarchy}
            onOpenOtherLocalities={() => setOtherLocalitiesOpen(true)}
            series={series}
          />
        ) : (
          <>
            {view === 'bars' ? (
              <>
                <div className="flex flex-wrap gap-x-4 gap-y-2 border-b border-border-subtle px-5 py-3 font-instrument text-xs text-textSecondary">
                  {series.map((item) => (
                    <span key={item.key} className="inline-flex min-w-0 items-center gap-1.5">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: item.color }} aria-hidden />
                      <span className="break-words">{item.label}</span>
                    </span>
                  ))}
                </div>
                <div className="overflow-x-auto px-3 py-4 sm:px-5">
                  <div className="min-w-[720px]" style={{ height: chartHeight }}>
                    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                      <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ bottom: 8, left: 18, right: 72, top: 4 }}
                      >
                        <CartesianGrid horizontal={false} stroke="rgba(20, 70, 42, 0.12)" />
                        <XAxis allowDecimals={false} type="number" tick={{ fill: 'currentColor', fontSize: 11 }} />
                        <YAxis
                          dataKey="city"
                          type="category"
                          width={185}
                          tick={{ fill: 'currentColor', fontSize: 11 }}
                          tickLine={false}
                        />
                        <Tooltip
                          content={<InterestRegionsTooltip metric={metric} series={series} />}
                          cursor={{ fill: 'rgba(20, 70, 42, 0.05)' }}
                          wrapperStyle={{ pointerEvents: 'none', zIndex: 40 }}
                        />
                        {series.map((item, index) => (
                          <Bar
                            key={item.key}
                            dataKey={item.key}
                            fill={item.color}
                            maxBarSize={24}
                            radius={index === series.length - 1 ? [0, 4, 4, 0] : 0}
                            stackId="campaigns"
                          >
                            {index === series.length - 1 && (
                              <LabelList
                                dataKey="total"
                                formatter={(value) => numberFormatter.format(Number(value ?? 0))}
                                position="right"
                              />
                            )}
                          </Bar>
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            ) : (
              <InterestRegionsDonut data={chartData} metric={metric} />
            )}

            <SeparatedLocationSummary
              datum={otherLocationDatum}
              description="Cidades informadas fora das regiões acompanhadas individualmente."
              label={OTHER_LOCALITIES_LABEL}
              metric={metric}
              onClick={() => setOtherLocalitiesOpen(true)}
              series={series}
            />
            <SeparatedLocationSummary
              datum={missingLocationDatum}
              description="Contatos sem cidade de interesse informada."
              label={MISSING_REGION_LABEL}
              metric={metric}
              series={series}
            />

            {selectedDatum && dominantSeries && (
              <div className="mx-4 mb-4 flex items-start gap-2 rounded-md border border-amber-200/70 bg-amber-50/60 px-3 py-2.5 font-instrument text-xs text-textPrimary dark:bg-amber-950/20 sm:mx-5">
                <i className="ri-flashlight-line mt-0.5 shrink-0 text-amber-700" aria-hidden />
                <p>
                  <strong>{selectedDatum.city}</strong> teve {numberFormatter.format(Number(selectedDatum.total))}{' '}
                  {metricUnit(metric, Number(selectedDatum.total))}; a maior contribuição foi de{' '}
                  <strong>{dominantSeries.label}</strong> ({numberFormatter.format(dominantSeries.value)}).
                </p>
              </div>
            )}
          </>
        )}
      </section>

      <OtherLocalitiesDialog
        isOpen={otherLocalitiesOpen}
        items={data.other_localities}
        onClose={() => setOtherLocalitiesOpen(false)}
      />
    </div>
  );
}
